import { useEffect, useMemo } from 'react';

import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { EditUserRequestType } from 'api/usersApi/types';
import { CommonModalType, ReturnComponentType } from 'common';
import { CommonModal } from 'components/Modals/CommonModal';
import {
  commonValidation,
  confirmPasswordValidation,
  updatePasswordValidation,
  emailValidation,
} from 'config';
import { CommonCharacters, Roles } from 'enums';
import {
  useAppSelector,
  useSelectCommunities,
  useSelectProperties,
  useTypedDispatch,
} from 'hooks';
import {
  editUser,
  fetchCurrentUser,
  fetchUsers,
  selectCertainUser,
  selectUser,
  UserType,
} from 'state';
import { setCertainUser } from 'state/actions';
import { Button, InputForm, Select } from 'ui-kit';
import { ISelectOption } from 'ui-kit/Select/Select';
import {
  checkIsCustomer,
  checkIsGoodStatus,
  chooseAvatarFromRole,
  chooseTitleFromRole,
} from 'utils';
import './ModalEditUser.scss'; // Changed import

type ModalEditUserType = CommonModalType & {
  userId: string; // userId is now mandatory
  isSelfManagement?: boolean;
};

const customerOptions: ISelectOption[] = [
  { value: Roles.COMMUNITY_CUSTOMER, label: 'Community' },
  { value: Roles.PROPERTY_CUSTOMER, label: 'Property' },
];

export const ModalEditUser = ({
  showModal,
  setShowModal,
  userId, // userId is now a top-level prop, not optional
  isSelfManagement = false,
}: ModalEditUserType): ReturnComponentType => {
  const dispatch = useTypedDispatch();
  const user = useAppSelector(selectCertainUser); // This should be the user to edit
  const currentUser = useAppSelector(selectUser); // This is the logged-in user

  // If isSelfManagement, we edit the currentUser, otherwise the user fetched by userId
  const formUser = isSelfManagement ? currentUser : user;

  const {
    selectOptions: selectPropertiesOptions,
    onChangeSelect: onChangePropertiesSelect,
    onFocusSelect: onFocusPropertiesSelect,
    selectedValue: selectedPropertiesValue,
    onMenuScrollToBottom: onMenuPropertiesScrollToBottom,
  } = useSelectProperties({ user: formUser });

  const {
    selectOptions: selectCommunitiesOptions,
    onChangeSelect: onChangeCommunitiesSelect,
    onFocusSelect: onFocusCommunitiesSelect,
    selectedValue: selectedCommunitiesValue,
    onMenuScrollToBottom: onMenuCommunitiesScrollToBottom,
  } = useSelectCommunities({ getOptions: false, user: formUser });

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
  } = useForm<EditUserRequestType>({
    // Consider if a specific EditUserRequestType is needed
    defaultValues: {
      email: '',
      password: CommonCharacters.HIDDEN_PASSWORD, // Default for edit, user can change if needed
      passwordConfirm: '',
      firstName: '',
      lastName: '',
      role: undefined,
    },
  });

  useEffect(() => {
    if (formUser && formUser.id) {
      // Populate form when formUser data is available
      reset({
        email: formUser.email ?? '',
        password: CommonCharacters.HIDDEN_PASSWORD, // Keep password hidden unless changed
        passwordConfirm: '',
        firstName: formUser.firstName ?? '',
        lastName: formUser.lastName ?? '',
        role: checkIsCustomer(formUser.role)
          ? (customerOptions.find(item => item.value === formUser.role)?.value as Roles)
          : undefined,
      });
    }
  }, [formUser, reset]);

  useEffect(() => {
    if (selectCommunitiesOptions.length === 0 && formUser?.id) {
      onMenuCommunitiesScrollToBottom();
    }
  }, [selectCommunitiesOptions, formUser, onMenuCommunitiesScrollToBottom]);

  useEffect(() => {
    if (selectPropertiesOptions.length === 0 && formUser?.id) {
      onMenuPropertiesScrollToBottom();
    }
  }, [selectPropertiesOptions, formUser, onMenuPropertiesScrollToBottom]);

  const closeEditUserModal = () => {
    dispatch(setCertainUser({} as UserType)); // Clear the selected user
    setShowModal(false);
    reset({
      // Reset form to default edit state
      email: '',
      password: CommonCharacters.HIDDEN_PASSWORD,
      passwordConfirm: '',
      firstName: '',
      lastName: '',
      role: undefined,
    });
  };

  const onSubmit: SubmitHandler<EditUserRequestType> = async ({
    email,
    password,
    passwordConfirm,
    firstName,
    lastName,
    role,
  }) => {
    // Password should only be sent if it's not the HIDDEN_PASSWORD placeholder
    const passwordToSend =
      password === CommonCharacters.HIDDEN_PASSWORD ? undefined : password;

    const status = await dispatch(
      editUser({
        id: userId, // Use the passed userId for the edit action
        firstName,
        lastName,
        email,
        password: passwordToSend,
        passwordConfirm,
        role:
          checkIsCustomer(formUser?.role) && typeof role === 'object' && role !== null
            ? (customerOptions.find(item => item.value === formUser?.role)
                ?.value as Roles)
            : formUser?.role,
        companyId: formUser?.company?.id || currentUser?.company?.id || '',
        communityIds:
          // @ts-ignore
          selectedCommunitiesValue && selectedCommunitiesValue.length > 0
            ? // @ts-ignore
              selectedCommunitiesValue.map(item => item.value)
            : [],
        propertiesIds:
          // @ts-ignore
          selectedPropertiesValue && selectedPropertiesValue.length > 0
            ? // @ts-ignore
              selectedPropertiesValue.map(item => item.value)
            : [],
      }),
    );

    if (checkIsGoodStatus(status)) {
      if (!isSelfManagement) {
        dispatch(fetchUsers(false));
      } else {
        dispatch(fetchCurrentUser());
      }
      closeEditUserModal();
    }
  };

  const headerModal = useMemo(() => {
    if (isSelfManagement) {
      return 'Edit My Profile';
    }

    return `Edit ${chooseTitleFromRole(formUser?.role) || 'User'}`;
  }, [isSelfManagement, formUser?.role]);

  const searchField = useMemo(() => {
    const currentRole = watch('role') || formUser?.role;

    if (currentRole === Roles.SUPER_ADMIN) {
      return null;
    }

    if (currentRole === Roles.COMMUNITY_CUSTOMER && !isSelfManagement) {
      return (
        <section className="ModalEditUser_Form_Search">
          <Select
            isMulti
            label="Communities"
            options={selectCommunitiesOptions}
            onChange={onChangeCommunitiesSelect}
            onFocus={onFocusCommunitiesSelect}
            value={selectedCommunitiesValue}
            onMenuScrollToBottom={onMenuCommunitiesScrollToBottom}
            placeholder="Select communities"
          />
        </section>
      );
    }

    if (currentRole === Roles.PROPERTY_CUSTOMER && !isSelfManagement) {
      return (
        <section className="ModalEditUser_Form_Search">
          <Select
            isMulti
            label="Properties"
            options={selectPropertiesOptions}
            onChange={onChangePropertiesSelect}
            onFocus={onFocusPropertiesSelect}
            value={selectedPropertiesValue}
            onMenuScrollToBottom={onMenuPropertiesScrollToBottom}
            placeholder="Select properties"
          />
        </section>
      );
    }

    return null;
  }, [
    onChangeCommunitiesSelect,
    onChangePropertiesSelect,
    onFocusCommunitiesSelect,
    onFocusPropertiesSelect,
    onMenuCommunitiesScrollToBottom,
    onMenuPropertiesScrollToBottom,
    selectCommunitiesOptions,
    selectPropertiesOptions,
    selectedCommunitiesValue,
    selectedPropertiesValue,
    formUser?.role,
    watch('role'),
    isSelfManagement,
  ]);

  return (
    <CommonModal closeModal={closeEditUserModal} showModal={showModal}>
      <div className="ModalEditUser">
        {' '}
        {/* Changed class name */}
        <header className="ModalEditUser_Header">
          {' '}
          {/* Changed class name */}
          {headerModal}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div onClick={closeEditUserModal} className="ModalEditUser_Header_Close">
            {' '}
            {/* Changed class name */}
            &#10006;
          </div>
        </header>
        <form className="ModalEditUser_Form" onSubmit={handleSubmit(onSubmit)}>
          {' '}
          {/* Changed class name */}
          <section>
            <img
              src={chooseAvatarFromRole(formUser?.role)} // Removed createdUserRole
              alt="user-logo"
              className="ModalEditUser_Form_Img"
            />
            <h4>{chooseTitleFromRole(formUser?.role)}</h4> {/* Removed createdUserRole */}
          </section>
          <section>
            <Controller
              control={control}
              name="firstName"
              rules={commonValidation}
              render={({ field }) => (
                <InputForm
                  label="First name"
                  className="ModalEditUser_Form_Input" /* Changed class name */
                  value={field.value}
                  onChange={e => field.onChange(e)}
                  error={errors.firstName?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="lastName"
              rules={commonValidation}
              render={({ field }) => (
                <InputForm
                  label="Last name"
                  className="ModalEditUser_Form_Input" /* Changed class name */
                  value={field.value}
                  onChange={e => field.onChange(e)}
                  error={errors.lastName?.message}
                />
              )}
            />
          </section>
          {checkIsCustomer(formUser?.role) && // Removed createdUserRole check
            !isSelfManagement && (
              <section>
                <div>Type</div>
                <Controller
                  control={control}
                  name="role"
                  rules={commonValidation}
                  render={({ field }) => (
                    <Select
                      value={customerOptions.find(item => item.value === field.value)}
                      onChange={e => field.onChange(e)}
                      options={customerOptions}
                      className="ModalEditUser_Form_Select" /* Changed class name */
                      error={errors.role?.message}
                      disabled={!!formUser?.role} // Disable role change if already set for existing customer
                    />
                  )}
                />
              </section>
            )}
          <section>
            <Controller
              control={control}
              name="email"
              rules={emailValidation}
              render={({ field }) => (
                <InputForm
                  label="Email"
                  className="ModalEditUser_Form_Input_Full" /* Changed class name */
                  value={field.value}
                  onChange={e => field.onChange(e)}
                  error={errors.email?.message}
                />
              )}
            />
          </section>
          <section>
            <Controller
              control={control}
              name="password"
              rules={updatePasswordValidation}
              render={({ field }) => (
                <InputForm
                  label={
                    <span>
                      Password
                      <span className="ModalEditUser_Form_Label_Small">
                        leave blank to keep current
                      </span>
                    </span>
                  }
                  className="ModalEditUser_Form_Input" /* Changed class name */
                  value={field.value}
                  onChange={e => field.onChange(e)}
                  error={errors.password?.message}
                  type="password"
                />
              )}
            />
            {isSelfManagement && (
              <Controller
                control={control}
                name="passwordConfirm"
                rules={
                  watch('password') !== CommonCharacters.HIDDEN_PASSWORD &&
                  confirmPasswordValidation
                } // Consider if this validation is appropriate for edit (e.g. allow empty for no change)
                render={({ field }) => (
                  <InputForm
                    label="Confirm Password"
                    className="ModalEditUser_Form_Input" /* Changed class name */
                    value={field.value}
                    onChange={e => field.onChange(e)}
                    error={errors.passwordConfirm?.message}
                    type="password"
                  />
                )}
              />
            )}
          </section>
          {searchField}
          <Button type="submit" className="ModalEditUser_Form_Submit">
            {' '}
            {/* Changed class name */}
            Save Changes
          </Button>
        </form>
      </div>
    </CommonModal>
  );
};
